import React, { FC } from 'react';
import { UserInfo } from '../../../common/model/dto/user-info';
import { useQuery } from '@tanstack/react-query';
import { getReproductiveStageForUser } from '../../../services/admin/admin-straw-stage.service';
import styled from 'styled-components';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import OvTable from '../molecules/OvTable';
import { StrawStageDataUtils } from '../../../common/utils/services/straw-stage-data-utils';
import { useTranslation } from 'react-i18next';
import OvLoadingIndicator from '../atoms/OvLoadingIndicator';
import OvErrorMessage from '../atoms/OvErrorMessage';
import { Alert, AlertTitle } from '@mui/material';

interface OvReproductiveStageDetailsProps {
  selectedUser?: UserInfo;
}

const OvReproductiveStageDetails: FC<OvReproductiveStageDetailsProps> = ({
  selectedUser,
}) => {
  const { t } = useTranslation();

  const {
    data: strawStageContent,
    isPending,
    isFetching,
    error,
  } = useQuery({
    queryKey: ['admin-reproductive-stage', selectedUser?.id],
    queryFn: () => getReproductiveStageForUser(selectedUser?.id ?? ''),
    retry: 3,
    enabled: !!selectedUser?.id,
  });

  const isLoading = isPending || isFetching;

  return (
    <Container>
      <InfoCard>
        {!isLoading && !error && strawStageContent?.straw_stage ? (
          <>
            <TableWrapper>
              <OvTable
                data={StrawStageDataUtils.mapStrawStageDataToTableData(
                  strawStageContent
                )}
              />
            </TableWrapper>

            {strawStageContent?.cycle_patterns_to_watch?.length ? (
              <Section>
                <SectionTitle>
                  {t('strawStage.sections.cyclePatternsToWatch')}
                </SectionTitle>
                <List>
                  {strawStageContent.cycle_patterns_to_watch.map(
                    (item, index) => (
                      <ListItem key={`cycle-${index}`}>{item}</ListItem>
                    )
                  )}
                </List>
              </Section>
            ) : null}

            {strawStageContent?.predicted_symptoms?.length ? (
              <Section>
                <SectionTitle>
                  {t('strawStage.sections.predictedSymptoms')}
                </SectionTitle>
                <List>
                  {strawStageContent.predicted_symptoms.map((item, index) => (
                    <ListItem key={`sym-${index}`}>{item}</ListItem>
                  ))}
                </List>
              </Section>
            ) : null}

            {strawStageContent?.care_options?.length ? (
              <Section>
                <SectionTitle>
                  {t('strawStage.sections.careOptions')}
                </SectionTitle>
                <List>
                  {strawStageContent.care_options.map((item, index) => (
                    <ListItem key={`care-${index}`}>{item}</ListItem>
                  ))}
                </List>
              </Section>
            ) : null}
          </>
        ) : (
          <StyledAlert severity="info">
            <AlertTitle>{t('strawStage.alert.title')}</AlertTitle>
            {t('strawStage.alert.description', {
              numberOfScanDays: strawStageContent?.total_scan_days,
            })}
          </StyledAlert>
        )}
      </InfoCard>

      {isLoading && <OvLoadingIndicator position="fixed" />}
      {error && <OvErrorMessage message={error.message} />}
    </Container>
  );
};

export default OvReproductiveStageDetails;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const InfoCard = styled.div`
  background: ${Colours.WHITE};
  box-shadow: ${Variables.boxShadow.defaultBox};
  border-radius: ${Variables.borderRadius.LARGE};
  padding: 1rem 1.25rem;
`;

const Section = styled.div`
  margin: 0.75rem 0 0 0.75rem;
`;

const SectionTitle = styled.h4`
  margin: 0 0 0.25rem 0;
  color: ${Colours.OV_BASE};
  font-weight: 600;
`;

const List = styled.ul`
  margin: 0.25rem 0 0 1.25rem;
  padding: 0;
`;

const ListItem = styled.li`
  margin: 0.125rem 0;
  line-height: 1.25;
  font-size: 0.875rem;
`;

const TableWrapper = styled.div`
  flex-grow: 1;
  align-self: flex-start;
  margin: 0 0 2rem 0.75rem;
  width: 100%;
`;

const StyledAlert = styled(Alert)`
  && {
    margin: 0.25rem 0 0.5rem 0;
    border-radius: ${Variables.borderRadius.LARGE};
    background-color: ${Colours.OV_BORDER_COLOR_V3};
    color: ${Colours.OV_BASE};

    .MuiAlertTitle-root {
      font-weight: bold !important;
    }
  }
`;
